import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { downloadFile } from '../../services/folder';
import { getAnalysis, detectAnnotations, getDetections, findFileInMcp, updateConicalClassification } from '../../services/analyses';
import { getEnabledOperations } from '../../services/operations';
import DetectionView from './DetectionView';
import AnalysisOrders from './AnalysisOrders';
import './AnalysisPreview.css';

const AnalysisPreview = ({ analysis: initialAnalysis, onClose }) => {
  const navigate = useNavigate();
  const [originalAnalysis] = useState(initialAnalysis);
  const [currentAnalysis, setCurrentAnalysis] = useState(initialAnalysis);
  const isViewingSimilar = currentAnalysis.hash !== originalAnalysis.hash;
  const analysis = currentAnalysis;
  const result = analysis.result;
  const fullJson = result?.full_json || result || {};
  const pageInfo = fullJson._page_info || {};
  const drawingPageNumbers = pageInfo.drawing_page_numbers || [];
  const hasMultipleDrawings = drawingPageNumbers.length > 1;

  const [selectedPage, setSelectedPage] = useState(null);
  const [pageImageUrl, setPageImageUrl] = useState(null);
  const [imageError, setImageError] = useState(null);
  const [imageRetry, setImageRetry] = useState(0);
  const [detections, setDetections] = useState(null);
  const [detectionImageSize, setDetectionImageSize] = useState(null);
  const [detectionsLoaded, setDetectionsLoaded] = useState(false);
  const [detectingAnnotations, setDetectingAnnotations] = useState(false);
  const [downloadSearching, setDownloadSearching] = useState(false);
  const [downloadError, setDownloadError] = useState(null);
  const [operationsEnabled, setOperationsEnabled] = useState(null);
  const detectionRef = useRef(null);
  const [detectionState, setDetectionState] = useState({});
  const [conicalSaving, setConicalSaving] = useState(false);

  const handleSetConical = async (newValue) => {
    if (!currentAnalysis?.hash || conicalSaving) return;
    setConicalSaving(true);
    try {
      const data = await updateConicalClassification(currentAnalysis.hash, newValue);
      setCurrentAnalysis((prev) => prev ? { ...prev, is_conical: data.is_conical } : prev);
    } catch (e) {
      console.error('Error updating conical classification:', e);
    } finally {
      setConicalSaving(false);
    }
  };

  const [mcpPath, setMcpPath] = useState(() => {
    try {
      const metadata = analysis.metadata_json ? JSON.parse(analysis.metadata_json) : {};
      return metadata.mcp_path || null;
    } catch {
      return null;
    }
  });

  useEffect(() => {
    let cancelled = false;
    getEnabledOperations()
      .then(({ operations }) => {
        if (!cancelled) setOperationsEnabled(Array.isArray(operations) && operations.length > 0);
      })
      .catch(() => {
        if (!cancelled) setOperationsEnabled(null);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    if (hasMultipleDrawings && selectedPage === null && drawingPageNumbers.length > 0) {
      setSelectedPage(drawingPageNumbers[0]);
    } else if (!hasMultipleDrawings && selectedPage === null) {
      setSelectedPage(drawingPageNumbers[0] || 1);
    }
  }, [hasMultipleDrawings, selectedPage, drawingPageNumbers]);

  const effectivePage = selectedPage || drawingPageNumbers[0] || 1;

  useEffect(() => {
    if (!effectivePage) return;
    let cancelled = false;
    let createdUrl = null;
    setPageImageUrl(null);
    setImageError(null);

    (async () => {
      try {
        const resp = await fetch(
          `${process.env.REACT_APP_API_URL}/v1/analyses/${analysis.hash}/image?page=${effectivePage}&include_deleted=true`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        if (cancelled) return;
        // Sin esta rama el fallo era mudo: ni imagen ni error, y el overlay de carga
        // se quedaba encendido para siempre.
        if (!resp.ok) {
          console.error(`Error loading page image: HTTP ${resp.status}`);
          setImageError(resp.status === 404
            ? 'No se encuentra el archivo original de este plano.'
            : 'No se ha podido cargar la imagen del plano.');
          return;
        }
        const blob = await resp.blob();
        if (cancelled) return;
        createdUrl = URL.createObjectURL(blob);
        setPageImageUrl(createdUrl);
      } catch (error) {
        console.error('Error loading page image:', error);
        if (!cancelled) setImageError('No se ha podido cargar la imagen del plano.');
      }
    })();

    return () => {
      cancelled = true;
      if (createdUrl) URL.revokeObjectURL(createdUrl);
    };
  }, [analysis.hash, effectivePage, imageRetry]);

  useEffect(() => {
    if (!effectivePage) return;
    let cancelled = false;
    setDetectionsLoaded(false);

    (async () => {
      try {
        const saved = await getDetections(analysis.hash, effectivePage);
        if (cancelled) return;
        setDetections(saved.detections || []);
        setDetectionImageSize(saved.image_size || null);
      } catch (error) {
        console.error('Error loading saved detections:', error);
        if (!cancelled) {
          setDetections([]);
          setDetectionImageSize(null);
        }
      } finally {
        if (!cancelled) setDetectionsLoaded(true);
      }
    })();

    return () => { cancelled = true; };
  }, [analysis.hash, effectivePage]);

  const handleViewSimilarAnalysis = async (similarHash) => {
    try {
      const similarAnalysis = await getAnalysis(similarHash, true);
      setCurrentAnalysis(similarAnalysis);
      setSelectedPage(null);
      setPageImageUrl(null);
      setDetections([]);
      setDetectionImageSize(null);
    } catch (error) {
      console.error('Error loading similar analysis:', error);
    }
  };

  const handleBackToOriginal = () => {
    setCurrentAnalysis(originalAnalysis);
    setSelectedPage(null);
    setPageImageUrl(null);
    setDetections([]);
    setDetectionImageSize(null);
  };

  const handleDetectAnnotations = useCallback(async () => {
    setDetectingAnnotations(true);
    try {
      const result = await detectAnnotations(analysis.hash, effectivePage);
      setDetections(result.detections || []);
      setDetectionImageSize(result.image_size || null);
    } catch (error) {
      console.error('Error detecting annotations:', error);
    } finally {
      setDetectingAnnotations(false);
    }
  }, [analysis.hash, effectivePage]);

  const handleDownloadExcel = async () => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_API_URL}/v1/analyses/${analysis.hash}/excel`,
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      if (response.ok) {
        const contentDisposition = response.headers.get('Content-Disposition');
        const fecha = new Date(analysis.created).toISOString().slice(0, 10).replace(/-/g, '');
        const baseName = analysis.filename.replace(/\.[^/.]+$/, '');
        let downloadFilename = `analisis_${baseName}_${fecha}.xlsx`;
        if (contentDisposition) {
          const match = contentDisposition.match(/filename=(.+)/);
          if (match && match[1]) downloadFilename = match[1];
        }
        const blob = await response.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url;
        a.download = downloadFilename;
        a.click();
        URL.revokeObjectURL(url);
      }
    } catch (error) {
      console.error('Error downloading Excel:', error);
    }
  };

  const handleDownloadOriginal = async () => {
    setDownloadError(null);
    try {
      let blob;
      if (mcpPath) {
        try { blob = await downloadFile(mcpPath); } catch { blob = null; }
      }
      if (!blob) {
        const response = await fetch(
          `${process.env.REACT_APP_API_URL}/v1/analyses/${analysis.hash}/original`,
          { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
        );
        if (response.ok) blob = await response.blob();
      }
      if (!blob && analysis.source === 'mcp') {
        setDownloadSearching(true);
        try {
          const result = await findFileInMcp(analysis.hash);
          if (result.found) {
            setMcpPath(result.new_path);
            blob = await downloadFile(result.new_path);
          }
        } finally {
          setDownloadSearching(false);
        }
      }
      if (!blob) {
        const msg = analysis.source === 'mcp'
          ? 'No se ha podido encontrar el archivo. Es posible que haya sido eliminado o movido fuera de la colección.'
          : 'No se ha podido encontrar el archivo original.';
        setDownloadError(msg);
        return;
      }
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = analysis.filename;
      a.click();
      URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading file:', error);
      setDownloadError('Error al descargar el archivo.');
    }
  };

  const handleGoToOriginalLocation = () => {
    if (mcpPath) {
      const folderPath = mcpPath.substring(0, mcpPath.lastIndexOf('/')) || '/';
      const fileName = mcpPath.substring(mcpPath.lastIndexOf('/') + 1);
      let mcpId = 'primary';
      try {
        const meta = analysis.metadata_json ? JSON.parse(analysis.metadata_json) : {};
        mcpId = meta.mcp_id || 'legacy';
      } catch {
        mcpId = 'legacy';
      }
      navigate(`/folder-browser?path=${encodeURIComponent(folderPath)}&highlight=${encodeURIComponent(fileName)}&mcp=${mcpId}`);
    }
  };

  return (
    <div className="preview-container">
      <div className="preview-header">
        <div className="preview-title-section">
          {isViewingSimilar && (
            <button className="btn-back" onClick={handleBackToOriginal}>
              ← Volver al original
            </button>
          )}
          <h1>{isViewingSimilar ? 'Análisis Similar' : 'Previsualización del Análisis'}</h1>
        </div>
        <div className="preview-actions">
          {analysis.source === 'mcp' && mcpPath && (
            <button className="btn-location" onClick={handleGoToOriginalLocation}>
              Ver ubicación
            </button>
          )}
          <button
            className="btn-location"
            onClick={() => window.open(`/similarity-search?hash=${analysis.hash}&compare=1`, '_blank')}
            title="Abre la comparativa de piezas similares de este análisis"
          >
            Comparar similares
          </button>
          <button className="btn-download" onClick={handleDownloadExcel}>
            Descargar análisis
          </button>
          <button className="btn-download" onClick={handleDownloadOriginal} disabled={downloadSearching}>
            {downloadSearching ? 'Buscando archivo...' : 'Descargar archivo'}
          </button>
          {downloadError && (
            <span className="download-error-msg">{downloadError}</span>
          )}
          <div className="preview-actions-separator" />
          {detectionState.hasAnalysisHash && (
            <>
              {(() => {
                const v = currentAnalysis?.is_conical;
                const label = v === 1 ? 'Cónico' : v === 0 ? 'Recto' : 'Sin clasificar';
                const cls = v === 1 ? 'conical-yes' : v === 0 ? 'conical-no' : 'conical-unknown';
                return (
                  <div className={`conical-badge ${cls}`} title="Tipo de pieza detectado por IA. Puedes corregirlo manualmente.">
                    <span className="conical-label">Tipo: {label}</span>
                    <select
                      className="conical-select"
                      value={v === 1 ? '1' : v === 0 ? '0' : ''}
                      onChange={(e) => {
                        const raw = e.target.value;
                        handleSetConical(raw === '1' ? 1 : raw === '0' ? 0 : null);
                      }}
                      disabled={conicalSaving}
                    >
                      <option value="">Sin clasificar</option>
                      <option value="1">Cónico</option>
                      <option value="0">Recto</option>
                    </select>
                  </div>
                );
              })()}
              <button className="btn-download" onClick={() => detectionRef.current?.downloadExcel()} disabled={detectionState.downloadingExcel}>
                Generar informe
              </button>
              <span className={`preview-autosave-status status-${detectionState.saveStatus || (detectionState.saving ? 'saving' : (detectionState.hasUnsavedChanges ? 'dirty' : 'idle'))}`}>
                {detectionState.saving && 'Guardando…'}
                {!detectionState.saving && detectionState.saveStatus === 'ok' && '✓ Guardado'}
                {!detectionState.saving && detectionState.saveStatus === 'error' && '⚠ Error al guardar'}
                {!detectionState.saving && !detectionState.saveStatus && detectionState.hasUnsavedChanges && '● Sin guardar'}
              </span>
            </>
          )}
          <button className="btn-detection-help" onClick={() => detectionRef.current?.openHelp()} title="Ayuda">?</button>
          <button className="btn-close" onClick={onClose}>✕</button>
        </div>
      </div>

      <AnalysisOrders analysisHash={analysis.hash} />

      {imageError && (
        <div className="preview-image-error">
          <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.8">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 8v5M12 16h.01" />
          </svg>
          <span className="preview-image-error-text">{imageError}</span>
          <span className="preview-image-error-hint">El resto del análisis sigue disponible.</span>
          <button className="preview-image-error-retry" onClick={() => setImageRetry(imageRetry + 1)}>
            Reintentar
          </button>
        </div>
      )}

      <div className="preview-body-wrapper">
        {(!detectionsLoaded || (!pageImageUrl && !imageError)) && (
          <div className="preview-loading-overlay">
            <div className="preview-loading-spinner" />
            <div className="preview-loading-text">Cargando análisis…</div>
          </div>
        )}
        <DetectionView
          ref={detectionRef}
          embeddedInPreview
          onStateChange={setDetectionState}
          analysisData={analysis}
          operationsEnabled={operationsEnabled}
          onViewSimilarAnalysis={handleViewSimilarAnalysis}
          onDetectAnnotations={handleDetectAnnotations}
          detectingAnnotations={detectingAnnotations}
          detections={detections || []}
          imageSize={detectionImageSize}
          imageSrc={pageImageUrl}
          onClose={onClose}
          analysisHash={analysis.hash}
          page={effectivePage}
        />
      </div>
    </div>
  );
};

export default AnalysisPreview;
